import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { getDrafts, deleteDraft, type Draft } from '@/api/inbox';
import { FileEdit, Trash2 } from 'lucide-react';
import { sanitizeEmailHtml } from '@/lib/sanitize';

function fmtDate(sec: number): string {
  return new Date(sec * 1000).toLocaleString();
}

function DraftPreview({ html }: { html: string }) {
  const safe = sanitizeEmailHtml(html);
  return (
    // B-22: contain:paint keeps quoted mail's position:fixed inside this box (see message-view).
    <div
      className="prose prose-invert max-w-none text-sm text-bmail-text [&_a]:text-bmail-gold [&_blockquote]:border-l [&_blockquote]:border-bmail-border [&_blockquote]:pl-2 [&_blockquote]:text-bmail-text-secondary"
      style={{ contain: 'paint', position: 'relative' }}
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

export function DraftsRoute() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['drafts'], queryFn: getDrafts });
  const [openId, setOpenId] = useState<string | null>(null);
  const drafts = data?.drafts || [];

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDraft(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] })
  });

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div className="border-b border-bmail-border px-4 py-4 md:px-6">
        <h1 className="flex items-center gap-2 text-xl font-bold text-bmail-text">
          <FileEdit size={20} /> Drafts
        </h1>
        <p className="mt-1 text-sm text-bmail-text-secondary">
          Unsent messages saved from the compose editor. Pick one up where you left off.
        </p>
      </div>

      {isLoading ? (
        null
      ) : drafts.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center p-10 text-center text-bmail-text-secondary">
          <FileEdit size={40} className="mb-3 opacity-40" />
          <p className="text-sm">No drafts. Hit “Save draft” while composing to keep one here.</p>
        </div>
      ) : (
        <ul className="divide-y divide-bmail-border">
          {drafts.map((d: Draft) => {
            const open = openId === d.id;
            return (
              <li key={d.id} className="px-4 py-3 md:px-6">
                <div className="flex items-center justify-between gap-2">
                  <button
                    type="button"
                    aria-expanded={open}
                    onClick={() => setOpenId(open ? null : d.id)}
                    className="min-w-0 flex-1 cursor-pointer text-left"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm text-bmail-text">
                        {d.to.length ? `To: ${d.to.join(', ')}` : '(no recipient)'}
                      </span>
                      <span className="shrink-0 text-xs text-bmail-text-secondary">{fmtDate(d.updatedAt)}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-bmail-text">{d.subject || '(no subject)'}</span>
                      {d.mailboxEmail && (
                        <span className="shrink-0 rounded bg-bmail-surface-hover px-1.5 py-0.5 text-[10px] text-bmail-text-secondary">{d.mailboxEmail}</span>
                      )}
                    </div>
                  </button>
                  <div className="flex shrink-0 gap-1">
                    <button
                      onClick={() => navigate(`/compose?draft=${d.id}`)}
                      className="rounded-lg bg-bmail-goldfill px-3 py-1.5 text-xs font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover transition-colors"
                    >
                      Continue
                    </button>
                    <button
                      onClick={() => deleteMutation.mutate(d.id)}
                      title="Delete draft"
                      className="rounded-lg p-2 text-bmail-text-secondary hover:text-bmail-error transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="mt-3 rounded-lg border border-bmail-border bg-bmail-surface p-3">
                    <DraftPreview html={d.bodyHtml} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
